<script lang="ts">
  import Skeleton from './ui/skeleton.svelte';
  import type MultiOptionSelect from './ui/select/multi-option-select.svelte';
  import ShopifyImg from './shopify-img.svelte';
  import WishlistButton from './wishlist-button.svelte';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';

  import { formatCurrency } from '$lib/utils/currency';
  import { createEventDispatcher, onDestroy, type ComponentProps } from 'svelte';
  import type { QuickAddToCartFragment } from '$gql/storefront/operations';
  import {
    splitTitleAndCategory,
    splitTitleAndCategoryByHandle
  } from '$lib/utils/shopify/splitTitleAndCategory';
  import { buildPath } from '$lib/utils/paths';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { AddToCartLocations } from '$lib/analytics';
  import {
    categoryCampaigns,
    getCampaignColors,
    tieredCampaign,
    tieredCampaignColors
  } from '$lib/stores/campaigns';
  import { page } from '$app/stores';
  import Icon from './ui/icon/icon.svelte';
  import AddToCart from './product-card/add-to-cart.svelte';
  import { flatOptions, parseColorSelectorMetafield } from './product-card/product-card.svelte';
  import Button from './ui/button.svelte';
  import { goto } from '$app/navigation';
  import { isFittingBoxEnabled, startVirtualTryOn } from '$lib/utils/fittingbox';
  import Link from './ui/link.svelte';
  import Glasses from './ui/icon/glasses.svelte';

  let className: string | undefined = undefined;
  export { className as class };
  export let comparePriceColor: 'default' | 'accent' = 'default';
  export let product: QuickAddToCartFragment | undefined | null;
  export let productHandle: string | undefined = undefined;
  export let loading: boolean | undefined = undefined;
  export let error: boolean | undefined = undefined;
  export let withImage = true;
  export let emptyStateImage: string | undefined = undefined;
  export let location: AddToCartLocations | undefined = undefined;
  export let addDrawerTargetId: string | undefined = undefined;
  const dispatch = createEventDispatcher<{ intersect: undefined }>();

  $: ({ title, category } = splitTitleAndCategory({
    title: product?.title || '',
    titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
  }));

  $: ({ title: titleByHandle, category: categoryByHandle } = splitTitleAndCategoryByHandle({
    handle: productHandle || '',
    titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
  }));

  // options
  $: options = product?.options;

  const getSelected = () => {
    const opt = options?.map(
      ({ name, values }) =>
        ({ value: values[0], group: name }) satisfies ComponentProps<
          MultiOptionSelect<string, { group: string; value: string }>
        >['value']
    );

    if (opt && opt.length > 1) {
      return opt;
    } else if (opt) {
      return opt[0];
    }
  };

  let selectedOptions = getSelected();

  $: if (!selectedOptions && options) selectedOptions = getSelected();

  $: hasOnlyDefaultVariant = options?.length === 1 && options[0].values.length === 1;

  $: selectedVariant = Array.isArray(selectedOptions)
    ? selectedOptions?.map(({ value }) => value).join(' / ')
    : selectedOptions && 'value' in selectedOptions
      ? selectedOptions.value
      : undefined;

  let selectedProductVariant: QuickAddToCartFragment['variants']['nodes'][number] | undefined =
    undefined;

  $: selectedProductVariant = hasOnlyDefaultVariant
    ? product?.variants?.nodes[0]
    : product?.variants?.nodes.find((variant) => variant.title === selectedVariant);

  // data
  $: currencyCode = selectedProductVariant?.price.currencyCode || 'EUR';
  $: price = selectedProductVariant?.price.amount;
  $: comparePrice = selectedProductVariant?.compareAtPrice?.amount;
  $: image = selectedProductVariant?.image || product?.featuredImage;
  $: isOnSale = !!(comparePrice && +comparePrice > +price);
  $: hasTieredCampaign =
    product && !!$tieredCampaign && !$tieredCampaign.isProductExcluded(product);

  $: hasCategoryCampaign =
    product &&
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => product && !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;
  $: categoryCampaign =
    product &&
    $categoryCampaigns?.find((campaign) => product && !campaign.isProductExcluded(product));

  let isBlackCampaignColor: boolean;
  let categoryAltTextColor: string | undefined = undefined;
  let categoryAltBackgroundColor: string | undefined = undefined;

  $: tieredAltTextColor = $tieredCampaignColors.tieredAltTextColor;
  $: tieredAltBackgroundColor = $tieredCampaignColors.tieredAltBackgroundColor;

  $: if (categoryCampaign?.campaign_color) {
    const categoryCampaignColors = getCampaignColors(categoryCampaign.campaign_color);
    isBlackCampaignColor = categoryCampaignColors.isBlackColor;
    categoryAltTextColor = categoryCampaignColors.altTextColor;
    categoryAltBackgroundColor = categoryCampaignColors.altBackgroundColor;
  }

  let added = false;
  let timeout: NodeJS.Timeout;
  $: if (added) {
    timeout = setTimeout(() => (added = false), 2000);
  }
  onDestroy(() => clearTimeout(timeout));

  let isAddToCardDrawerOpen = false;

  let selectedVariantOption = (() => {
    if (product && parseColorSelectorMetafield(product)) {
      // get first available variant
      const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
      if (availableVariant) {
        return flatOptions(product.options).find((option) => option === availableVariant.title);
      }
      return flatOptions(product.options)[0];
    }
  })();

  $: showFittingBox =
    product &&
    splitTitleAndCategory({
      title: product.title,
      titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
    }).category == 'Sunglasses' &&
    isFittingBoxEnabled();
</script>

<div
  class={cn('relative', className)}
  use:intersectionObserver={{
    callback: (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        dispatch('intersect');
      }
    }
  }}
  style:--tieredAltBackgroundColor={tieredAltBackgroundColor}
  style:--tieredAltTextColor={tieredAltTextColor}
  style:--categoryAltBackgroundColor={categoryAltBackgroundColor}
  style:--categoryAltTextColor={categoryAltTextColor}
>
  {#if loading}
    <div class="relative flex flex-col gap-3 bg-highlight/88 rounded-md p-1.5 backdrop-blur">
      <div class="flex flex-1 gap-2.5">
        <Skeleton class="h-[72px] w-[72px] rounded-xs" />
        <div class="grow">
          <Skeleton class="mb-1 h-4 w-1/2 rounded-xs" />
          <Skeleton class="h-4 w-1/4 rounded-xs" />
          <Skeleton class="h-4 w-8 rounded-xs" />
        </div>
      </div>
    </div>
  {:else if !loading && !error && product}
    <div class={cn('flex flex-col gap-3 bg-highlight p-1.5 rounded-md backdrop-blur')}>
      <div class="flex flex-1 gap-2.5">
        {#if image && withImage}
          {@const { url: src, altText: alt, width, height } = image}
          <a
            href={$buildPath({ path: `/products/${product?.handle}` })}
            class="transition-all hover:opacity-80 relative h-[72px] w-[72px]"
          >
            <div class="absolute inset-0">
              <ShopifyImg
                {src}
                {alt}
                {width}
                {height}
                widths={[96]}
                sizes="96px"
                class="relative block w-24 h-full shrink-0 select-none overflow-hidden rounded-xs border border-primary/4 bg-primary/2 object-cover"
              />
            </div>
          </a>
        {/if}

        <div class="grow">
          <div class={cn('text-sm flex justify-between flex-col h-full')}>
            <div class="flex items-center gap-1">
              <a
                href={$buildPath({ path: `/products/${product?.handle}` })}
                class="max-w-fit block pt-1 quick-add-title-block"
              >
                <p class="font-semibold max-w-fit">{title}</p>
                <p class={cn('text-primary/40')}>
                  {category}
                </p>
              </a>
            </div>

            <div
              class={cn(
                'flex lining-nums tabular-nums items-end justify-between',
                comparePriceColor === 'accent' && isOnSale && 'flex-row-reverse justify-end'
              )}
            >
              <a
                href={$buildPath({
                  path: `/products/${product?.handle}`
                })}
                class={cn('flex gap-1 items-baseline pb-1 quick-add-price-block')}
              >
                <p
                  class={cn(
                    'whitespace-nowrap font-semibold',
                    hasCategoryCampaign && 'text-[var(--categoryAltTextColor)]',
                    hasCategoryCampaign && isBlackCampaignColor && 'text-accent'
                  )}
                >
                  {$formatCurrency(
                    currencyCode,
                    hasCategoryCampaign && categoryCampaign
                      ? categoryCampaign?.getDiscountedPrice(+price)
                      : +price
                  )}
                </p>
                {#if isOnSale && comparePrice && !hasCategoryCampaign}
                  <p class={cn('whitespace-nowrap font-semibold', 'text-primary/40 line-through')}>
                    {$formatCurrency(currencyCode, +comparePrice)}
                  </p>
                {/if}
                {#if hasCategoryCampaign && categoryCampaign}
                  <p class={cn('whitespace-nowrap font-semibold', 'text-primary/40 line-through')}>
                    {$formatCurrency(currencyCode, +price)}
                  </p>
                {/if}
              </a>
              <div
                class={cn(
                  'flex items-center gap-0 justify-end',
                  hasTieredCampaign &&
                    $tieredCampaign &&
                    'bg-[var(--tieredAltBackgroundColor)] border-2 border-[var(--tieredAltBackgroundColor)] rounded-sm'
                )}
              >
                {#if hasTieredCampaign && $tieredCampaign}
                  <p
                    class={cn(
                      'text-sm px-1.5 flex items-center rounded-[6px] text-[var(--tieredAltTextColor)] '
                    )}
                  >
                    {$t('product-card.get-for', {
                      price: $formatCurrency(
                        currencyCode,
                        $tieredCampaign.getDiscountedPrice(+price)
                      ),
                      discount: $tieredCampaign.discount
                    })}
                  </p>
                {/if}
                <AddToCart
                  {product}
                  colorSelectorSelectedVariant={selectedVariantOption}
                  bind:open={isAddToCardDrawerOpen}
                  targetId={addDrawerTargetId}
                  class="flex"
                  {location}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="absolute top-1.5 right-1.5 flex gap-2 items-center w-max">
        {#if showFittingBox}
          <Link
            class=" pb-1 text-xs flex gap-1 w-max"
            on:click={() => startVirtualTryOn(selectedProductVariant?.barcode)}
          >
            <Glasses class="w-[13px] h-[13px]" notAnimated />
            {$t('glasses.try-on')}</Link
          >
        {/if}
        <WishlistButton variant="icon" size="xs" productId={product.id} />
      </div>
    </div>
  {:else if !product && productHandle}
    <div class="flex gap-3 bg-highlight/80 p-1.5 rounded-md backdrop-blur capitalize">
      <div
        class="h-[72px] w-[72px] rounded-xs bg-primary/4 flex justify-center items-center overflow-hidden border border-primary/4"
      >
        {#if emptyStateImage}
          <img src={emptyStateImage} alt={'Product image'} class="object-cover" />
        {:else}
          <Icon size="lg" icon="diamond" class="text-primary opacity-40" />
        {/if}
      </div>

      <div class="grow text-sm">
        <div class={cn('text-sm flex flex-col h-full justify-between')}>
          <div class="max-w-fit block pt-1">
            <p class="font-semibold max-w-fit">{titleByHandle}</p>
            <p class={cn('text-primary/40')}>
              {categoryByHandle}
            </p>
          </div>

          <div class={cn('flex gap-1 lining-nums tabular-nums items-end justify-between')}>
            <div class={cn('flex gap-1 pb-1 items-baseline')}>
              <p class={cn('whitespace-nowrap ')}>{$t('product-card.unavailable')}</p>
            </div>
            <Button
              name="add-to-cart"
              {loading}
              size="xs"
              variant={'accent'}
              on:click={() => {
                goto(
                  $buildPath({
                    path: `/collections`
                  })
                );
              }}>{$t('product-card.show-jewelry')}</Button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
